<!-- SearchPatientOrderResults.vue -->
<script lang="ts" setup>
    import SearchBase from './SearchBase.vue';
    import { ref, onMounted, watch } from 'vue';
    import { ISearchPatientTestResultsItem } from '../../lib/Api/EmrOrderEntry';
    import { ISearchBaseInputs } from '../../lib/Models/SearchModel';
    import * as choiceApi from '../../lib/ChoiceApi'
    import { ISearchModeProps } from './SearchUtils';
    import { toInt } from '../../lib/Convert/String';

    const props = withDefaults(defineProps<ISearchModeProps & {
        patientId: number,
        defaultDaysSinceCollected?: number
    }>(), {
        label: "Patient Order Results",
        defaultDaysSinceCollected: 90
    });

    const emit = defineEmits<{
        "onChange": [selectedItem: TResult],
        "update:searchMode": [newMode: string]
    }>();

    onMounted(()=>{
        internalDefaultSearchText.value = props.defaultSearchText ?? "";
    });    

    watch(() => props.defaultSearchText, (newVal, _oldVal) =>{
        internalDefaultSearchText.value = newVal ?? "";
    });

    const displayColumn = "selectionText";
    const searchColumns = [
        { title: "Collected", element: "collectionDate" },
        { title: "Lab", element: "clientLabName" },
        { title: "Order", element: "orderItemsString", isHtml: true }
    ];
    const labResultDelimitSymbol = "<i class='fa-solid fa-flask text-muted me-1 ms-2'></i>";
    

    type TResult = ISearchPatientTestResultsItem & { orderItemsString: string, selectionText: string };
    const internalDefaultSearchText = ref<string>("");    
    const searchResults = ref<TResult[]>([]);
    
    async function performSearchBase(inputs: ISearchBaseInputs)  {
        const days = toInt(inputs.searchText) ?? props.defaultDaysSinceCollected ?? 90;
        internalDefaultSearchText.value = days.toString();
        inputs.searchText = internalDefaultSearchText.value;
        
        searchResults.value = [];
        const apiResults = await choiceApi.emrOrderEntry.searchPatientTestResults(props.patientId, days);
        if (apiResults?.length)
        {
            // Convert order items into a string with a delimitor in front of each order & add selection text
            const uiResults: TResult[] = apiResults.map(item => {
                    const uiItem = item as TResult;
                    uiItem.orderItemsString = labResultDelimitSymbol + item.orderItems.join(` ${labResultDelimitSymbol}`);
                    uiItem.selectionText = `${item.collectionDate} (${item.clientLabName})`;
                    return uiItem;
                });
            searchResults.value = uiResults ?? [];
        }
    }    

    function onChange(selectedItem: TResult) {
        emit("onChange", selectedItem);
        internalDefaultSearchText.value = selectedItem?.selectionText ?? "";
    }

</script>

<template>
    <SearchBase
        ref="baseComp"
        :displayColumn="displayColumn"
        :columns="searchColumns" 
        :searchResults="searchResults"
        @performSearchBase="performSearchBase"
        @onChange="onChange"

        :label="props.label"
        :placeholder="props.placeholder"  
        :defaultSearchText="internalDefaultSearchText"
        :processing="processing"
        :enableAutoSearch="props.enableAutoSearch"
        :showClearSearch="props.showClearSearch"
        :resetFocus="props.resetFocus" />
</template>